import clsx from "clsx";
import { fmtDate } from "../format";
import { LOCK_EXPLANATIONS, LOCK_SHORT_HINTS } from "../vocabulary";
import type { TrackingLockKind } from "../types";

/**
 * The R / V lock icon — identify AND act, in place. Shared by the fleet row
 * and the hotel page's locks card so the same glyph means the same thing on
 * both, and the hover carries what the lock DOES before anyone clicks.
 *
 * `revealOnHover` is the fleet table's behavior (an unplaced lock is invisible
 * until the row is hovered, so 107 dashed outlines don't shout); the hotel
 * page passes it off — there, the two locks are the card's whole subject.
 */
export function LockButton({
  lock,
  facts,
  onClick,
  revealOnHover = false,
}: {
  lock: TrackingLockKind;
  facts: { locked: boolean; auto: boolean; appliedAt: string | null };
  onClick: () => void;
  revealOnHover?: boolean;
}) {
  const short = lock === "rules" ? "R" : "V";
  const label = lock === "rules" ? "Rules lock" : "Validator lock";
  const title = facts.locked
    ? `${label} — ${facts.auto ? "auto-applied" : "applied"} ${fmtDate(facts.appliedAt)}.\n${LOCK_EXPLANATIONS[lock]}\nClick to remove.`
    : `Apply ${label.toLowerCase()} — ${LOCK_SHORT_HINTS[lock]}.`;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      data-tip={title}
      className={clsx(
        "inline-flex items-center justify-center h-6 rounded text-[11px] font-semibold leading-none transition-all",
        facts.locked
          ? "gap-1 px-2 bg-warning-light text-warning-dark"
          : clsx(
              "w-6 border border-dashed border-gray-300 text-gray-300 hover:border-gray-400 hover:text-gray-500",
              revealOnHover && "opacity-0 group-hover:opacity-100",
            ),
      )}
    >
      <span>{short}</span>
      {facts.locked && <span className="text-[10px] leading-none">🔒</span>}
    </button>
  );
}
